import { useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMatchMedia, BreakPointsEnum, useResizeObserver, isDefined } from "@perxis-auto/shared";
import "react-router";
import { APP } from "../../../shared/config/app.js";
import "../../../shared/config/env.js";
import "../../../shared/config/events.js";
import "redux";
import { useAppDispatch } from "../../../shared/lib/store/hooks/useAppDispatch.js";
import { useAppSelector } from "../../../shared/lib/store/hooks/useAppSelector.js";
import "../../../shared/lib/store/hooks/useAppStore.js";
import { selectors } from "../../../features/comparison/model/index.js";
import "../../../shared/lib/store/createAppApi.js";
import "../../../shared/lib/store/createAppAsyncThunk.js";
import "../../../shared/lib/store/createAppSelector.js";
import "../../../shared/lib/store/createAppSlice.js";
import { useGetComparableVehicles } from "../../../features/comparison/lib/useGetComparableVehicles.js";
import { useConstructorContext } from "../../../shared/context/constructor.js";
import "@perxis-auto/shared/lib";
import { useMergedConstructorItemProps } from "../../../shared/lib/constructor/hooks/useMergedConstructorItemProps.js";
import "../../../shared/lib/constructor/mergeConstructor.js";
import "react/jsx-runtime";
import "@perxis-auto/ui-kit";
import { useAppNavigate } from "../../../shared/lib/router/hooks/useAppNavigate.js";
import "@perxis-auto/shared/lib/lib/is/isObject";
import { GET_DEFAULT_CONTAINER_PROPS } from "../config/props.js";
import { ComparisonShiftDirectionEnum } from "../../../features/comparison/model/types.js";
import { actions } from "../../../features/comparison/model/slice.js";
const useContainer = (item) => {
  var _a, _b, _c, _d;
  const { containerSelector, redirects, ...props } = useMergedConstructorItemProps(item, GET_DEFAULT_CONTAINER_PROPS);
  const { layout, widgets } = useConstructorContext();
  const { t } = useTranslation(APP.name);
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const totalCompared = useAppSelector((state) => selectors.selectTotal(state.comparison));
  const {
    currentData: comparableVehiclesData,
    preparedData: { selectedComparableVehicles = [], sortedComparableVehicles = [] },
    isLoading: isLoadingQuery,
    isUninitialized: isUninitializedQuery,
    isSuccess: isSuccessQuery
  } = useGetComparableVehicles();
  const isMediaMdMax = useMatchMedia(BreakPointsEnum.mdMax);
  const sideNavRef = useRef(null);
  const { height: sideNavHeight } = useResizeObserver({ ref: sideNavRef });
  const [isSticky, setIsSticky] = useState(false);
  useEffect(() => {
    const widgetContainer = document.querySelector(containerSelector);
    if (!isDefined(widgetContainer)) {
      return;
    }
    const handleScroll = () => {
      window.requestAnimationFrame(() => {
        const rect = widgetContainer.getBoundingClientRect();
        if (rect.top > 0 && isSticky) {
          widgetContainer.style.position = "relative";
          setIsSticky(false);
        }
      });
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        const { bottom } = entry.boundingClientRect;
        if (bottom < 0 && !isSticky) {
          widgetContainer.style.position = "sticky";
          setIsSticky(true);
        }
      },
      { root: null, threshold: 0 }
    );
    observer.observe(widgetContainer);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, [containerSelector, isSticky]);
  const onBackClick = useCallback(() => {
    navigate(redirects.onBackClick.to, redirects.onBackClick.options);
  }, [navigate, redirects.onBackClick.options, redirects.onBackClick.to]);
  const onNextClick = useCallback(
    (itemId) => {
      if (itemId) {
        return dispatch(
          actions.shift({
            direction: ComparisonShiftDirectionEnum.Right,
            itemId
          })
        );
      }
      dispatch(
        actions.shiftAll({
          direction: ComparisonShiftDirectionEnum.Right
        })
      );
    },
    [dispatch]
  );
  const onPrevClick = useCallback(
    (itemId) => {
      if (itemId) {
        return dispatch(
          actions.shift({
            direction: ComparisonShiftDirectionEnum.Left,
            itemId
          })
        );
      }
      dispatch(
        actions.shiftAll({
          direction: ComparisonShiftDirectionEnum.Left
        })
      );
    },
    [dispatch]
  );
  const getBottomNavIndex = useCallback(
    (itemId) => {
      return ((comparableVehiclesData == null ? void 0 : comparableVehiclesData.items.findIndex((i) => i.id === itemId)) || 0) + 1;
    },
    [comparableVehiclesData == null ? void 0 : comparableVehiclesData.items]
  );
  const isLoading = isLoadingQuery || isUninitializedQuery;
  const isEmpty = !totalCompared && isSuccessQuery;
  const isPrevDisabled = ((_a = selectedComparableVehicles == null ? void 0 : selectedComparableVehicles[0]) == null ? void 0 : _a.id) === ((_b = sortedComparableVehicles == null ? void 0 : sortedComparableVehicles[0]) == null ? void 0 : _b.id);
  const isNextDisabled = ((_c = selectedComparableVehicles == null ? void 0 : selectedComparableVehicles[selectedComparableVehicles.length - 1]) == null ? void 0 : _c.id) === ((_d = sortedComparableVehicles[sortedComparableVehicles.length - 1]) == null ? void 0 : _d.id);
  const isBottomNavHidden = !isMediaMdMax || isSticky || isLoading;
  const isSideNavHidden = !!isMediaMdMax || isLoading;
  return {
    ...props,
    t,
    layout,
    widgets,
    isPrevDisabled,
    isNextDisabled,
    getBottomNavIndex,
    onPrevClick,
    onNextClick,
    isSticky,
    sideNavRef,
    sideNavHeight,
    isBottomNavHidden,
    isSideNavHidden,
    redirect: redirects.link,
    total: totalCompared,
    items: selectedComparableVehicles,
    isLoading,
    isEmpty,
    onBackClick
  };
};
export {
  useContainer
};
